import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import StyledTable from "src/components/Table";
import { GridColDef } from "@mui/x-data-grid";
import { AppDispatch, RootState } from "src/store";
import { deleteVendor, getVendors, reset } from "../redux/VendorSlice";
import { IconButton, Box, Typography } from "@mui/material";
import { Add, Eye } from "iconsax-react";
import colors from "src/theme/colors";
import styled from "@emotion/styled";
import { StyledButton } from "src/components/Button";
import ConfirmModal from "src/components/Modal";
import { PageTemplate } from "src/layout/components/PageTemplate";
import { ListBoundary } from "src/components/ListBoundary/ListBoundary";

const VendorTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [vendorsData, setVendorsData] = useState([] as any);
  const [vendorsMetaData, setVendorsMetaData] = useState([] as any);
  const [selected, setSelected] = useState([] as any);
  const [selectedId, setSelectedId] = useState([0]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const {
    vendors,
    vendorsMeta,
    isError,
    isDeleteError,
    isSuccess,
    isDeleteSuccess,
    message,
    deleteMessage,
    isLoading,
    isDeleteLoading,
  } = useSelector((state: RootState) => state.vendor);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(message, { variant: "error" });
    }
    if (isSuccess) {
      if (message) {
        enqueueSnackbar(message, { variant: "success" });
      } else {
        setVendorsData(vendors);
        setVendorsMetaData(vendorsMeta);
      }
    }
    return () => {
      dispatch(reset());
    };
  }, [isError, isSuccess, message]);

  useEffect(() => {
    if (isDeleteError) {
      enqueueSnackbar(deleteMessage, { variant: "error" });
    }
    if (isDeleteSuccess) {
      enqueueSnackbar(deleteMessage, { variant: "success" });
      dispatch(
        getVendors({
          page:
            vendorsData.length <= 1 || selectedId.length >= vendorsData.length
              ? page > 1
                ? page - 1
                : 1
              : page,
        })
      ).then(() => setIsDeleteModalOpen(false));

      if (vendorsData.length <= 1 || selectedId.length >= vendorsData.length) {
        setPage(page > 1 ? page - 1 : 1);
      }
      setSelected([]);
      setSelectedId([]);
    }
    return () => {
      dispatch(reset());
    };
  }, [isDeleteError, isDeleteSuccess, deleteMessage]);

  useEffect(() => {
    dispatch(
      getVendors({
        page: page,
      })
    );
    setSelected([]);
    setSelectedId([]);
  }, [page]);

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 80, filterable: false },
    {
      field: "name",
      headerName: "Full Name",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "code",
      headerName: "Vendor Code",
      flex: 0.8,
      minWidth: 140,
    },
    {
      field: "mobile",
      headerName: "Mobile Number",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "company_name",
      headerName: "Company Name",
      flex: 1,
      minWidth: 260,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 260,
    },
    {
      field: "zip_code",
      headerName: "Zip Code",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 140,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="space-around"
          alignItems="center"
          flexDirection="row"
        >
          <IconButton
            onClick={() =>
              navigate("/vendors/vendor-information", {
                state: { id: params?.id },
              })
            }
          >
            <Eye color={colors.gray[600]} size="24" />
          </IconButton>
          <IconButton
            onClick={() =>
              navigate("/vendors/vendor-edit", {
                state: { vendorData: params.row },
              })
            }
          >
            <img
              src={process.env.PUBLIC_URL + "/assets/icons/Edit.svg"}
              alt="edit"
            />
          </IconButton>
          {/* <IconButton>
            <img
              src={process.env.PUBLIC_URL + "/assets/icons/Remove.svg"}
              alt="delete"
              onClick={() => {
                setSelectedId([Number(params?.id)]);
                setIsDeleteModalOpen(true);
              }}
            />
          </IconButton> */}
        </Box>
      ),
    },
  ];

  const onDelete = (selected: any) => {
    const deleteValue = {
      vendor_ids: selected,
    };
    dispatch(deleteVendor(deleteValue));
  };

  return (
    <PageTemplate
      crumbs={[
        {
          label: "vendors list",
        },
      ]}
    >
      <ListBoundary
        isEmpty={vendorsData.length === 0 || !vendorsData}
        isDataLoading={isLoading}
        fallbackImageURL="/assets/images/emptyStates/Vendors-List EmptyState.svg"
        description="Register The First Vendor!"
        buttonLabel="New Vendor"
        onClick={() => navigate("/vendors/vendor-new")}
      >
        <StyledHeader>
          <StyledButton
            variant="contained"
            endIcon={<Add size="24" color={colors.gray[850]} />}
            onClick={() => navigate("/vendors/vendor-new")}
          >
            {t("vendors.vendorsTable.tableNewButton")}
          </StyledButton>
          {/* {selected.length !== 0 ? (
            <StyledButton
              variant="contained"
              endIcon={
                <img
                  src={process.env.PUBLIC_URL + "/assets/icons/removeWhite.svg"}
                  alt="delete"
                />
              }
              onClick={() => {
                setSelectedId(selected);
                setIsDeleteModalOpen(true);
              }}
              color="error"
            >
              Remove Vendors
            </StyledButton>
          ) : (
            <StyledButton
              variant="contained"
              endIcon={<Add size="24" color={colors.gray[850]} />}
              onClick={() => navigate("/vendors/vendor-new")}
            >
              {t("vendors.vendorsTable.tableNewButton")}
            </StyledButton>
          )} */}
        </StyledHeader>
        <StyledTable
          disableColumnMenu
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableVirtualization
          disableSelectionOnClick
          loading={isLoading}
          checkboxSelection
          experimentalFeatures={{ newEditingApi: true }}
          rows={vendorsData}
          columns={columns}
          onSelectionModelChange={(e) => setSelected(e)}
          onPageChange={(e) => setPage(e + 1)}
          page={page - 1}
          paginationMode="server"
          rowCount={vendorsMetaData?.pagination?.total_items}
        />
      </ListBoundary>
      <ConfirmModal
        maxWidth={420}
        title={selectedId.length > 1 ? "Remove Vendors" : "Remove Vendor"}
        iconName={"ModalRemove"}
        open={isDeleteModalOpen}
        isLoading={isDeleteLoading || isLoading}
        onDismiss={() => setIsDeleteModalOpen(false)}
        onConfirm={() => onDelete(selectedId)}
        confrimText={"Remove"}
        cancelText={"Cancel"}
      >
        <Typography
          textAlign="center"
          fontSize={14}
          fontWeight={400}
          color={colors.gray[450]}
        >
          Are you sure you want to remove{" "}
          {selectedId.length > 1 ? "these Vendors" : "this Vendor"}? By clicking
          on{" "}
          <b>“{selectedId.length > 1 ? "Remove Vendors" : "Remove Vendor"}”</b>,
          all data related to{" "}
          {selectedId.length > 1 ? "these Vendors" : "this Vendor"} will also be
          removed.
        </Typography>
      </ConfirmModal>
    </PageTemplate>
  );
};

export default VendorTable;

const StyledHeader = styled(Box)`
  display: flex;
  flex-direction: row-reverse;
  padding-bottom: 24px;
`;
